import * as React from 'react';
import {useEffect} from 'react';
import {FormattedMessage} from 'react-intl';

import {useDeviceStore, selectIsMobile} from 'features/ui/store/useDeviceStore';
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {FILTERS_MAX_ROOMS, FILTERS_MIN_ROOMS} from 'features/search/filters/constants';
import {
    useSearchStore,
    selectMaxRooms,
    selectMinRooms
} from 'features/search/store/useSearchStore';

import 'components/organisms/ResidenceSearch/ResidenceFilters/filters/_roomSelector.scss';

import {Counter} from '@atoms/FormElements/Counter';

interface Props {
    customMinRooms?: number; // Valfri: Anpassat minimum antal rum
    customMaxRooms?: number; // Valfri: Anpassat maximum antal rum
    onChangeRoomsFilter?: (minRooms: number, maxRooms: number) => void; // Valfri: Anpassad ändringsfunktion
    triggerReset?: boolean; // Återställ trigger
}

export const RoomSelectorV2: React.FunctionComponent<Props> = ({
                                                                   customMinRooms,
                                                                   customMaxRooms,
                                                                   onChangeRoomsFilter,
                                                                   triggerReset
                                                               }) => {
    // Zustand store
    const storeMinRooms = useSearchStore(selectMinRooms);
    const storeMaxRooms = useSearchStore(selectMaxRooms);
    const changeRoomFilter = useSearchStore(state => state.changeRoomFilter);
    const isMobile = useDeviceStore(selectIsMobile);

    // Use custom values if provided, otherwise use store values
    const minRooms = customMinRooms ?? storeMinRooms;
    const maxRooms = customMaxRooms ?? storeMaxRooms;

    useEffect(() => {
        if (triggerReset && isMobile) {
            // Återställ standardvärden
            handleChangeRoomsFilter(FILTERS_MIN_ROOMS, FILTERS_MAX_ROOMS);
        }
    }, [triggerReset]);

    const handleChangeRoomsFilter = (minRooms: number, maxRooms: number) => {
        if (onChangeRoomsFilter) {
            // Om en funktion skickas via props, använd den
            onChangeRoomsFilter(minRooms, maxRooms);
        } else {
            // Annars använd Zustand store
            changeRoomFilter(minRooms, maxRooms);
        }
    };

    const onMinRoomsChange = (count: number) => {
        handleChangeRoomsFilter(count, maxRooms);
    };

    const onMaxRoomsChange = (count: number) => {
        handleChangeRoomsFilter(minRooms, count);
    };

    return (
        <div className="room-selector">
            <div className="room-selector__counter-container">
                <span>
                    <FormattedMessage id="jm.components.ResidenceFilters.minRooms" />
                </span>
                <Counter
                    id={getFormattedMessage('jm.components.ResidenceFilters.minRooms')}
                    onChange={onMinRoomsChange}
                    value={minRooms}
                    minValue={FILTERS_MIN_ROOMS}
                    maxValue={maxRooms !== FILTERS_MAX_ROOMS ? maxRooms : FILTERS_MAX_ROOMS}
                    maxValueAffix={maxRooms !== FILTERS_MAX_ROOMS ? '' : '+'}
                />
            </div>

            <div className="room-selector__divider">
                <span>-</span>
            </div>

            <div className="room-selector__counter-container">
                <span>
                    <FormattedMessage id="jm.components.ResidenceFilters.maxRooms" />
                </span>
                <Counter
                    id={getFormattedMessage('jm.components.ResidenceFilters.maxRooms')}
                    onChange={onMaxRoomsChange}
                    value={maxRooms}
                    minValue={minRooms !== FILTERS_MIN_ROOMS ? minRooms : FILTERS_MIN_ROOMS}
                    maxValue={FILTERS_MAX_ROOMS}
                    maxValueAffix="+"
                />
            </div>
        </div>
    );
};
